import {Subject, BehaviorSubject, combineLatest} from 'rxjs';
import {skipUntil, switchMap, map} from 'rxjs/operators';
import {ITEM_TRADE_TYPE, ORDER_STATUS, ORDER_TYPE} from '@/modules/assets/map';
import {timeUtil} from '@/helper/time';
import {numUtil} from '@/helper/num';
import {calcCurrentQty} from '@/helper/contract';
import {mc} from '@/core';
import {contracts$} from '@/streams/config/contract';
import {currencies$} from '@/streams/config/currency';
import {assetsApi} from '@/api/assets';
import {browser$} from '@/streams/browser';
import {qtyTypeBeforeV2$, qtyTypeV2$} from '@/streams/contract';
import {formatterUpgradedBeforeAfter} from '@/modules/assets/fund-details/util';
import {HistoryQueryUtaState} from '@/modules/assets/const';

export const loading$ = new Subject();
export const query$ = new Subject();

const orders$ = new BehaviorSubject({total: 0, rows: []});

export const orderList$ = combineLatest([orders$, contracts$, currencies$, qtyTypeBeforeV2$, qtyTypeV2$]).pipe(map(convertData));

query$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadOrderList(query))
  )
  .subscribe();

async function loadOrderList({currency, offset, symbol, ordStatus, ordType, isPerpetualV2, utaState = HistoryQueryUtaState.beforeUpgrade}) {
  loading$.next(true);
  const query = {
    currency,
    symbol,
    offset,
    ordStatus,
    ordType,
    limit: 10,
    withCount: true,
  };

  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      end: unifiedAccountUpgradedTime,
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: unifiedAccountUpgradedTime,
    })
  );

  const {data, error} = isPerpetualV2 ? await assetsApi.orderListV2(query2) : await assetsApi.orderList(query2);
  loading$.next(false);
  if (data) {
    orders$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

function convertData([orders, contracts, currencies, qtyTypeBeforeV2, qtyTypeV2]) {
  const {rows, total} = orders;
  let list = convertOrdersHistory({rows, contracts, currencies, qtyTypeBeforeV2, qtyTypeV2});
  list = list.filter(Boolean);
  return {rows: list, total};
}

export function convertOrdersHistory({rows, contracts, currencies, qtyTypeBeforeV2, qtyTypeV2}) {
  qtyTypeBeforeV2 = qtyTypeBeforeV2 || qtyTypeBeforeV2$.getValue();
  qtyTypeV2 = qtyTypeV2 || qtyTypeV2$.getValue();

  return rows.map((item) => {
    const contract = contracts.find((contract) => contract.symbol === item.symbol);
    if (!contract) {
      return null;
    }
    const {isPerpetualV2, pricePrecision, priceScale, settleCurrencyDisplay: settleCurrency, symbolV2, baseCurrency} = contract;
    const currency = currencies.find((o) => o.currency === contract.settleCurrency);
    if (!currency) {
      return null;
    }
    const {qtyPrecision, valueScale} = currency;
    const data = {
      createdTime: timeUtil.formatUTCDateTime(item.createdAt),
      symbol: item.symbol,
      displaySymbol: `${symbolV2} ${settleCurrency}-Margin`,
      status: ORDER_STATUS[item.ordStatus] || '--',
      orderType: genType(item),
      orderId: item.orderId,
      bizError: item.bizError,
      side: item.side,
      ordType: item.ordType,
      tradeType: item.tradeType,
      execPriceEp: item.execPriceEp,
    };

    if (isPerpetualV2) {
      const qtyPrice = Number(item.execPriceRp) !== 0 ? item.execPriceRp : item.priceRp;
      Object.assign(data, {
        orderQty: calcCurrentQty(contract, item.orderQtyRq, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
        orderPrice: genOrderPrice(item.priceRp, item.ordType, pricePrecision, 0),
        orderValue: genValue(Number(item.cumValueRv), 0, qtyPrecision, settleCurrency),
        triggerPrice: genTriggerPrice2(item),
        execQty: calcCurrentQty(contract, item.execQtyRq, item.priceRp, qtyTypeBeforeV2, qtyTypeV2),
        leavesQty: calcCurrentQty(contract, item.leavesQtyRq, item.priceRp, qtyTypeBeforeV2, qtyTypeV2),
        posSide: item.posSide,
        execPriceEp: Number(item.execPriceRp),
        priceEp: Number(item.priceRp),
        orderQtyRq: Number(item.orderQtyRq),
        displayQtyRq: Number(item.displayQtyRq),
        icebergQty: item.displayQtyRq + baseCurrency,
      });
      return data;
    }

    const qtyPrice = calcQtyPrice(item, pricePrecision, priceScale);
    Object.assign(data, {
      orderQty: `${item.orderQty === 0 ? 'PositionSize' : (item.side === 1 ? '' : '-') + calcCurrentQty(contract, item.orderQty, qtyPrice, qtyTypeBeforeV2, qtyTypeV2)}`,
      orderPrice: genOrderPrice(item.priceEp, item.ordType, pricePrecision, priceScale),
      orderValue: genValue(item.cumValueEv, valueScale, qtyPrecision, settleCurrency),
      triggerPrice: genTriggerPrice(item, pricePrecision, priceScale),
      execQty: calcCurrentQty(contract, item.execQty, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
      leavesQty: calcCurrentQty(contract, item.leavesQty, qtyPrice, qtyTypeBeforeV2, qtyTypeV2),
      priceEp: item.priceEp,
      orderQtyRq: Number(item.orderQtyRq),
      displayQtyRq: Number(item.displayQtyRq),
      icebergQty: item.displayQtyRq + 'Cont',
    });
    return data;
  });
}

function calcQtyPrice(item, pricePrecision, priceScale) {
  const price = Number(item.execPriceEp) !== 0 ? item.execPriceEp : item.priceEp;
  return numUtil.floor(price, pricePrecision, priceScale);
}

function genOrderPrice(price, ordType, pricePrecision, priceScale) {
  if ([1, 3, 5, 14].indexOf(ordType) > -1) {
    return 'Market';
  }

  return price > 0 ? numUtil.floor(price, pricePrecision, priceScale) : '-';
}

function genValue(valueEv, valueScale, qtyPrecision, settleCurrency) {
  return numUtil.floor(valueEv, qtyPrecision, valueScale) + ' ' + settleCurrency;
}

function genTriggerPrice(item, pricePrecision, priceScale) {
  const {stopDirection, stopPxEp, trigger} = item;
  if (stopDirection === 0 || stopPxEp === 0) {
    return '--';
  }

  const value = numUtil.floor(stopPxEp, pricePrecision, priceScale);
  const unit = trigger === 1 ? '(MP)' : trigger === 2 ? '(IP)' : '(LP)';
  return value + unit;
}
function genTriggerPrice2(item) {
  const {stopDirection, stopPxRp, trigger} = item;
  if (stopDirection === 0 || Number(stopPxRp) === 0) {
    return '--';
  }

  const unit = trigger === 1 ? '(MP)' : trigger === 2 ? '(IP)' : '(LP)';
  return stopPxRp + unit;
}

function genType(item) {
  const {ordType, tradeType, pegPriceType} = item;
  if (ordType === 0) {
    return ITEM_TRADE_TYPE[tradeType];
  }
  if ([3, 9].includes(ordType) && [5, 6, 7, 8].includes(pegPriceType)) {
    // 追踪委托订单展示额外处理
    return ORDER_TYPE[-12];
  }
  return ORDER_TYPE[ordType] || '--';
}
